import {
  useTable,
  type ReactTable,
  type CellData,
  type ColumnDef,
  type RowData,
  type TableOptions,
  type TableState,
  type Table_Internal,
} from '@tanstack/react-table';
import clsx from 'clsx';
import {
  forwardRef,
  useEffect,
  useRef,
  type JSX,
  type PropsWithChildren,
  type Ref,
} from 'react';
import { useIntl } from 'react-intl';

import { useDataTableConfigElement } from '../hooks/useDataTableConfig.js';
import { DataTableDefaultCell } from './components/DataTableDefaultCell.js';
import { DataTableEmptyState } from './components/DataTableEmptyState.js';
import { DataTableVirtualizationContainer } from './components/DataTableVirtualizationContainer.js';
import { DataTableSelectedRowsActions } from './components/TableActions/DataTableSelectedRowsActions.js';
import { DataTableSelectionChip } from './components/TableActions/DataTableSelectionChip.js';
import { DataTableTableActions } from './components/TableActions/DataTableTableActions.js';
import { DataTableTableActionsWrapper } from './components/TableActions/DataTableTableActionsWrapper.js';
import { DataTableColumnOrderSettings } from './components/Toolbar/ColumnSettings/DataTableColumnOrderSettings.js';
import { DataTableColumnPinningSettings } from './components/Toolbar/ColumnSettings/DataTableColumnPinningSettings.js';
import { DataTableColumnSettingsModal } from './components/Toolbar/ColumnSettings/DataTableColumnSettingsModal.js';
import { DataTableColumnSettingsTrigger } from './components/Toolbar/ColumnSettings/DataTableColumnSettingsTrigger.js';
import { DataTableColumnVisibilitySettings } from './components/Toolbar/ColumnSettings/DataTableColumnVisibilitySettings.js';
import { DataTableToolbar } from './components/Toolbar/DataTableToolbar.js';
import {
  DataTableColumnSettingsContextProvider,
  DataTableContextProvider,
} from './contexts/DataTableContext.js';
import * as styles from './DataTable.sty.js';
import { useColumnFontStyle } from './features/ColumnFontStyle/ColumnFontStyle.js';
import { useColumnOrder } from './features/ColumnOrder/useColumnOrder.js';
import { useColumnPinning } from './features/ColumnPinning/useColumnPinning.js';
import { useColumnSizing } from './features/ColumnSizing/useColumnSizing.js';
import { useColumnVisibility } from './features/ColumnVisibility/useColumnVisibility.js';
import { useDataTableConfigRef } from './features/Config.js';
import { _getDevToolsSlot, DataTableDevTools } from './features/DevTools.js';
import { DataTableDownload } from './features/Download/Download.js';
import { useDownloadData } from './features/Download/useDownload.js';
import { DataTableFeatures } from './features/features.js';
import { useGanttChart } from './features/GanttChart/useGanttChart.js';
import { useLayout } from './features/Layout/useLayout.js';
import { DataTableGlobalLineWrapAction } from './features/LineWrap/LineWrap.js';
import { useColumnLineWrap } from './features/LineWrap/useLineWrap.js';
import { useLoadingState } from './features/LoadingState/useLoadingState.js';
import { useMeterBarChartMaxValuesCalc } from './features/MeterBarChart/MeterBarChart.js';
import { createPaginatedRowModel } from './features/Pagination/pagination-row-model.js';
import { DataTablePaginationInternal } from './features/Pagination/PaginationInternal.js';
import {
  DataTablePagination,
  usePagination,
} from './features/Pagination/usePagination.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { Grid } from '../../layouts/grid/Grid.js';
import { DataTableExpandableRowWrapper } from './features/RowDetails/components/ExpandableRowWrapper.js';
import { createRowDetailsRowModel } from './features/RowDetails/details-row-model.js';
import {
  DataTableExpandableRowTemplate,
  useRowDetails,
} from './features/RowDetails/RowDetails.js';
import { useRowIdentification } from './features/RowIdentification/RowIdentification.js';
import { useRowInteractivity } from './features/RowInteractivity/useRowInteractivity.js';
import { createRowOrderRowModel } from './features/RowOrder/row-order-model.js';
import { useRowOrder } from './features/RowOrder/useRowOrder.js';
import { useRowSelection } from './features/RowSelection/useRowSelection.js';
import { createSortedRowModel } from './features/Sorting/sorted-row-model.js';
import { useSorting } from './features/Sorting/useSorting.js';
import { createExpandedRowModel } from './features/SubRows/subrows-row-model.js';
import { useSubRows } from './features/SubRows/useSubRows.js';
import * as thresholdStyles from './features/Thresholds/Thresholds.sty.js';
import { useThresholds } from './features/Thresholds/useThresholds.js';
import { DataTableCellActions } from './features/UserActions/CellActions.js';
import { DataTableColumnActions } from './features/UserActions/ColumnActions.js';
import { DataTableRowActions } from './features/UserActions/RowActions.js';
import { useUserActions } from './features/UserActions/useUserActions.js';
import { useVisualVariant } from './features/VisualVariants/useVisualVariants.js';
import { useBuiltInColumns } from './hooks/useBuiltInColumns.js';
import { useColumnRemeasure } from './hooks/useColumnRemeasure.js';
import { useColumns } from './hooks/useColumns.js';
import { useDataTableSlots } from './hooks/useDataTableSlots.js';
import { usePaginationEmptyPageCorrection } from './hooks/usePaginationEmptyPageCorrection.js';
import { usePaginationSlot } from './hooks/usePaginationSlot.js';
import { useTableRootResize } from './hooks/useTableRootResize.js';
import { useToolbarSlot } from './hooks/useToolbarSlot.js';
import type {
  DataTableColumnDef,
  DataTableProps,
  DataTableRef,
  DataTableRowData,
} from './public.api.js';
import { Intent as CoreIntent } from '../../core/slots/Intent/Intent.js';
import { extractDataAttributes } from '../utils/extract-data-attributes.js';
import { createCoreRowModel } from './features/createCoreRowModel.js';
import { table_autoResetPageIndex } from './features/Pagination/Pagination.js';
import type { TDataTableFeatures } from './features/types/features.types.js';
import { dataTableMessages } from './messages.js';

const COMPONENT_NAME = 'DataTable';

/**
 * Limits the DataTable subscription to only the required parts of the table store.
 * Most fields here are mutated directly via `baseAtoms.*.set()` — things like
 * drag-and-drop and menus that bypass React entirely. `columnResizing` is the
 * exception: it is managed by TanStack's own `columnResizingFeature`, but
 * DataTable reads `table.atoms.columnResizing?.get()` directly in render and
 * must re-render when it changes.
 *
 * Fields like sorting, pagination, columnOrder, and columnVisibility are left
 * out because they go through `useControllableState` and already cause a
 * React re-render on their own — no need to subscribe twice.
 *
 * Defined at module level (not inline) so the function reference is stable.
 */
const dataTableStoreSelector = (state: TableState<TDataTableFeatures>) => ({
  columnResizing: state.columnResizing,
  draggingRowInfo: state.draggingRowInfo,
  draggedHeaderColumn: state.draggedHeaderColumn,
  floatingMenuInfo: state.floatingMenuInfo,
  openMenuRowId: state.openMenuRowId,
  ganttAnnotationsSelection: state.ganttAnnotationsSelection,
});

function InternalDataTable<TData extends DataTableRowData>(
  props: PropsWithChildren<DataTableProps<TData>>,
  forwardedRef: Ref<DataTableRef>,
): JSX.Element {
  const {
    children,
    data: dataProps,
    columns: columnsProps,
    style: consumerStyle,
    className: consumerClassName,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const intl = useIntl();

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const { 'aria-label': ariaLabel, ...restAriaLabelingProps } =
    useAriaLabelingProps(restProps);
  const defaultAriaLabel =
    ariaLabel === undefined &&
    restAriaLabelingProps['aria-labelledby'] === undefined
      ? intl.formatMessage(dataTableMessages.dataTableLabel)
      : undefined;
  const dataAttributes = extractDataAttributes(restProps);

  const slots = useDataTableSlots(children);
  const {
    toolbarSlots,
    toolbarContent,
    toolbarItemProps,
    toolbarRemainingProps,
    openColumnSettings,
    columnSettingsModalRef,
  } = useToolbarSlot(slots.toolbarProps);
  const {
    paginationConsumerId,
    paginationDataTestId,
    paginationConsumerStyle,
    paginationConsumerClassName,
    paginationDataAttributes,
  } = usePaginationSlot(slots.pagination);

  // Hook used to adapt column definition for internal usage
  const [columns, consumerColumnDefMap] = useColumns(
    // Required cast to align the public facing generic DataTableRowDatas `unknown` type with the tanstack internal RowData type.
    columnsProps as unknown as DataTableColumnDef<DataTableRowData, CellData>[],
  );

  const configFullWidthOption = useDataTableConfigElement('fullWidth');
  const fullWidth = props.fullWidth ?? configFullWidthOption;

  const configFullHeightOption = useDataTableConfigElement('fullHeight');
  const fullHeight = props.fullHeight ?? configFullHeightOption;

  const options: TableOptions<
    typeof DataTableFeatures,
    Exclude<TData, unknown>
  > = {
    _features: DataTableFeatures,
    columns: columns as ColumnDef<
      typeof DataTableFeatures,
      Exclude<TData, unknown>,
      unknown
    >[],
    data: dataProps as unknown as Exclude<TData, unknown>[],
  };

  useRowIdentification(props, options);
  useLayout(props, options);
  useColumnLineWrap(props, options);
  useColumnFontStyle(props, options);
  useThresholds(props, options);
  useVisualVariant(props, options);
  useUserActions(
    props,
    // Required cast to align DataTableRowDatas any and the RowData type.
    options as TableOptions<typeof DataTableFeatures, RowData>,
    consumerColumnDefMap,
  );
  usePagination(props, options);
  useLoadingState(props, options);
  useColumnSizing(props, options);
  useSorting(props, options);
  useColumnVisibility(props, options);
  useColumnOrder(props, options);
  useRowSelection(props, options);
  useSubRows(props, options);
  useRowDetails(props, options);
  useRowInteractivity(props, options);
  useGanttChart(props, options);
  useMeterBarChartMaxValuesCalc(props, options);
  useRowOrder(props, options);
  useColumnPinning(props, options);
  useDownloadData(
    // DEPRECATION: [APPDEV-16236] BREAKING-CHANGE: Remove deprecated onDownloadData and DataTableDownloadProps.
    // After removing the deprecated prop, consider changing the signature to `useDownloadData(toolbarItemProps, options)`
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    { onDownloadData: toolbarItemProps.onDownloadData ?? props.onDownloadData },
    options,
  );

  // Adds the builtin columns depending on which features are enabled
  // eslint-disable-next-line react-hooks/immutability
  options.columns = useBuiltInColumns(options);
  const table = useTable<TDataTableFeatures, Exclude<TData, unknown>>(
    {
      _rowModels: {
        coreRowModel: createCoreRowModel(),
        expandedRowModel: createExpandedRowModel(),
        sortedRowModel: createSortedRowModel(),
        paginatedRowModel: createPaginatedRowModel(),
        detailsRowModel: createRowDetailsRowModel(),
        rowOrderRowModel: createRowOrderRowModel(),
      },
      ...options,
    },
    dataTableStoreSelector,
  );

  // Tanstack v9 - This effect is now necessary because the autoReset after the row model updates now also fires immediately.
  const initialRender = useRef(true);
  useEffect(() => {
    if (initialRender.current === true) {
      initialRender.current = false;
      return;
    }

    table_autoResetPageIndex(
      table as unknown as Table_Internal<TDataTableFeatures, RowData>,
    );
    // Explicitly not adding table to the dependency array to avoid infinite loop, as the table instance is stable and the function is also stable due to useCallback in the Pagination feature.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProps]);

  const tableRef = useDataTableConfigRef(
    forwardedRef,
    columnSettingsModalRef,
    table,
    props,
  );

  const rootRef = useTableRootResize(table.remeasureRows);
  const mergedRefs = useMergeRefs([rootRef, tableRef]);

  useColumnRemeasure(table);
  usePaginationEmptyPageCorrection(table);

  // Gets the passed ReactTableDevtools in order to make debugging easier.
  const DevTools = _getDevToolsSlot(props, table);

  return (
    <>
      <div
        ref={mergedRefs}
        className={clsx(
          styles.dataTable,
          {
            [styles.fullWidth]: fullWidth,
            [styles.notResizing]:
              table.atoms.columnResizing?.get().isResizingColumn === false,
          },
          {
            [thresholdStyles.hasPillThresholds]:
              table.options.hasThresholds &&
              table.options.rowThresholds?.some(
                (threshold) => threshold.type === 'pill',
              ),
          },
          consumerClassName,
        )}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        aria-label={ariaLabel ?? defaultAriaLabel}
        {...restAriaLabelingProps}
        {...behavioralTrackingProps}
        {...dataAttributes}
        data-dt-component={COMPONENT_NAME}
      >
        <DataTableContextProvider value={table}>
          {(toolbarContent ||
            slots.tableActions ||
            slots.selectedRowsActions) && (
            <Grid
              gridTemplateColumns="1fr auto"
              paddingBottom={4}
              gap={toolbarContent ? 8 : 0}
            >
              <DataTableTableActionsWrapper
                tableActions={slots.tableActions}
                selectedRowsActions={slots.selectedRowsActions}
              />
              {/* We need to actually render the toolbar here to trigger rerenders once the table context updates */}
              <DataTableColumnSettingsContextProvider
                value={{ openColumnSettings }}
              >
                <DataTableToolbar {...toolbarRemainingProps}>
                  {toolbarContent}
                </DataTableToolbar>
              </DataTableColumnSettingsContextProvider>
            </Grid>
          )}
          <DataTableColumnSettingsModal
            ref={columnSettingsModalRef}
            visibilitySettings={toolbarSlots.visibilitySettings}
            columnOrderSettings={toolbarSlots.columnOrderSettings}
            columnPinningSettings={toolbarSlots.columnPinningSettings}
            resetColumnVisibility={toolbarItemProps.resetColumnVisibility}
            resetColumnOrder={toolbarItemProps.resetColumnOrder}
            resetColumnPinning={toolbarItemProps.resetColumnPinning}
            visibleColumnLimits={toolbarItemProps.visibleColumnLimits}
          />

          <DataTableVirtualizationContainer
            // Need to cast this one here in order to make sure that it allows
            // instantiating with the same Subtype of RowData
            table={table as unknown as ReactTable<TDataTableFeatures, RowData>}
            emptyState={slots.emptyState}
            fullHeight={fullHeight ?? false}
          />

          {table.options.enablePagination === true ? (
            <DataTablePaginationInternal
              id={paginationConsumerId}
              data-testid={paginationDataTestId}
              className={paginationConsumerClassName}
              style={paginationConsumerStyle}
              {...paginationDataAttributes}
            />
          ) : null}
        </DataTableContextProvider>
      </div>
      {DevTools}
    </>
  );
}

InternalDataTable.displayName = COMPONENT_NAME;

const DataTableFwd = /* @__PURE__ */ forwardRef(InternalDataTable);

/**
 * The `DataTable` is a component for building tables which organize information
 * into rows and columns.
 * @public
 */
export const DataTable = Object.assign(DataTableFwd, {
  Pagination: DataTablePagination,
  ColumnActions: DataTableColumnActions,
  CellActions: DataTableCellActions,
  RowActions: DataTableRowActions,
  SelectedRowsActions: DataTableSelectedRowsActions,
  DefaultCell: DataTableDefaultCell,
  EmptyState: DataTableEmptyState,
  TableActions: DataTableTableActions,
  DevTools: DataTableDevTools,
  Toolbar: DataTableToolbar,
  VisibilitySettings: DataTableColumnVisibilitySettings,
  ColumnOrderSettings: DataTableColumnOrderSettings,
  ColumnPinningSettings: DataTableColumnPinningSettings,
  ColumnSettingsTrigger: DataTableColumnSettingsTrigger,
  SelectionChip: DataTableSelectionChip,
  DownloadData: DataTableDownload,
  Intent: CoreIntent,
  ExpandableRow: DataTableExpandableRowTemplate,
  ExpandableRowWrapper: DataTableExpandableRowWrapper,
  LineWrap: DataTableGlobalLineWrapAction,
});
